import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../bootstrap/Button.jsx';
import ProjectSelectorButton from '../../../common/ProjectSelectorButton.jsx';
import styles from '../advanced_filter/AdvancedFilterWidget.module.scss';
import {
  setMode,
  clearProjectFilter,
  loadMyProjectIds,
  loadPreset,
  selectProjectFilterMode,
  selectProjectFilterPresetId,
  selectActiveProjectIds,
  setActiveProjectIds,
} from '../../redux/projectFilterSlice.js';
import {
  setCrossDatasourceFilterModel,
  removeCrossDatasourceFilterModel,
} from '../../redux/datasourceSlice.js';
import { buildProjectFilterModel } from './projectFilterModel.js';

const ALIGNMENT = {
  'top-left':      { alignItems: 'flex-start', justifyContent: 'flex-start' },
  'top-center':    { alignItems: 'flex-start', justifyContent: 'center' },
  'top-right':     { alignItems: 'flex-start', justifyContent: 'flex-end' },
  'center-left':   { alignItems: 'center', justifyContent: 'flex-start' },
  'center':        { alignItems: 'center', justifyContent: 'center' },
  'center-right':  { alignItems: 'center', justifyContent: 'flex-end' },
  'bottom-left':   { alignItems: 'flex-end', justifyContent: 'flex-start' },
  'bottom-center': { alignItems: 'flex-end', justifyContent: 'center' },
  'bottom-right':  { alignItems: 'flex-end', justifyContent: 'flex-end' },
};

function ProjectFilterWidget({ widgetId }) {
  const dispatch = useDispatch();
  const mode = useSelector(selectProjectFilterMode);
  const activePresetId = useSelector(selectProjectFilterPresetId);
  const activeProjectIds = useSelector(selectActiveProjectIds);

  const settings = useSelector(state => state.dashboard.widgets[widgetId]?.settings || {});
  const showAll = settings.showAll !== false;
  const showMyProjects = settings.showMyProjects !== false;
  const presets = settings.presets || [];
  const datasourceMappings = settings.datasourceMappings || [];
  const buttonSize = settings.buttonSize || 'md';
  const buttonAlignment = settings.buttonAlignment || 'center';
  const singleProjectFilter = settings.singleProjectFilter || false;

  const filterId = `project-filter-${widgetId}`;

  // Inject AG Grid cross-filter whenever resolved project IDs change (my_projects and preset modes)
  useEffect(() => {
    if (activeProjectIds.length === 0) return;
    
    datasourceMappings.forEach(({ datasourceId, columnId, valueKey }) => {
      if (!datasourceId || !columnId) return;
      const filterModel = buildProjectFilterModel(columnId, activeProjectIds, valueKey || 'identifier');
      if (filterModel) {
        dispatch(setCrossDatasourceFilterModel({ datasourceId, filterId, filterModel }));
      }
    });
  }, [activeProjectIds]);

  // Clear cross-filters when mode resets to 'all'
  useEffect(() => {
    if (mode === 'all') {
      dispatch(removeCrossDatasourceFilterModel({ filterId }));
    }
  }, [mode]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      dispatch(removeCrossDatasourceFilterModel({ filterId }));
      dispatch(clearProjectFilter());
    };
  }, []);

  const handleAll = () => {
    dispatch(clearProjectFilter());
  };

  const handleMyProjects = () => {
    if (mode === 'my_projects') {
      dispatch(clearProjectFilter());
      return;
    }
    dispatch(setMode('my_projects'));
    dispatch(loadMyProjectIds());
  };

  const handlePreset = (preset) => {
    if (mode === 'preset' && activePresetId === preset.id) {
      dispatch(clearProjectFilter());
      return;
    }
    dispatch(setMode('preset'));
    dispatch(loadPreset({ presetId: preset.id }));
  };

  const handleProjectSelect = (selectedOptions) => {
    if (selectedOptions.length === 0) {
      dispatch(clearProjectFilter());
    } else {
      const newProjects = selectedOptions.map(({value, label}) => ({id: value, label: label}));
      dispatch(setActiveProjectIds(newProjects));
    }
  };

  const selectedProjectOptions = useMemo(() => {
    if (mode === 'custom') {
      return activeProjectIds.map(({id, label}) => ({value: id, label: label}));
    } else {
      return [];
    };
  }, [activeProjectIds, mode]);

  return (
    <div
      className={styles['filter-group-container']}
      style={{ ...(ALIGNMENT[buttonAlignment] || {}) }}
    >
      <div className={styles['btn-group-wrapper']}>
        <div className='btn-group'>
          {showAll && (
            <Button
              size={buttonSize}
              type=''
              onClick={handleAll}
              className={mode === 'all' ? styles['active-btn'] : styles['inactive-btn']}
            >
              {I18n.t('all')}
            </Button>
          )}
          {showMyProjects && (
            <Button
              size={buttonSize}
              type=''
              onClick={handleMyProjects}
              className={mode === 'my_projects' ? styles['active-btn'] : styles['inactive-btn']}
            >
              {I18n.t('my_projects')}
            </Button>
          )}
          {presets.map((preset) => (
            <Button
              key={preset.id}
              size={buttonSize}
              type=''
              onClick={() => handlePreset(preset)}
              className={
                mode === 'preset' && activePresetId === preset.id
                  ? styles['active-btn']
                  : styles['inactive-btn']
              }
            >
              {preset.label}
            </Button>
          ))}
          {singleProjectFilter &&
            <ProjectSelectorButton
              handleOnChangeSelectorInput={handleProjectSelect}
              rowSelectionMode='singleRow'
              type=''
              className={mode === 'custom' ? styles['active-btn'] : styles['inactive-btn']}
              data={selectedProjectOptions}
            >
              {selectedProjectOptions.length === 1 ? selectedProjectOptions[0].label : `${I18n.t('choose_project')}...`}
            </ProjectSelectorButton>
          }
        </div>
      </div>
    </div>
  );
}

export default ProjectFilterWidget;
